import {
  dismissOnboarding,
  getOnboardingProgress,
  type OnboardingProgress,
} from './OnboardingChecklist';
import {
  expiredLicenceCopy,
  openLicenceSubscribePage,
} from '@application/licence/LicenceExpiryCopy';

const WELCOME_SEEN_KEY = 'nexxus.welcome.v1';

export interface WelcomeModalOptions {
  trial?: boolean;
  daysLeft?: number;
  expired?: boolean;
  cabinetMode?: boolean;
}

function loadSeen(): boolean {
  try {
    return localStorage.getItem(WELCOME_SEEN_KEY) === '1';
  } catch {
    return false;
  }
}

function markSeen(): void {
  try {
    localStorage.setItem(WELCOME_SEEN_KEY, '1');
  } catch {
    /* private mode */
  }
}

function checklistItem(done: boolean, label: string): string {
  return `<li style="margin:6px 0">${done ? '✓' : '○'} ${label}</li>`;
}

function buildBody(progress: OnboardingProgress, opts: WelcomeModalOptions): string {
  const parts: string[] = [];

  if (opts.expired) {
    const copy = expiredLicenceCopy(opts.trial !== false);
    parts.push(
      `<p style="margin:0 0 12px;padding:10px 12px;border-radius:6px;background:#7f1d1d;color:#fff;font-size:13px">${copy.body}</p>`,
    );
  } else if (opts.trial) {
    const days = opts.daysLeft ?? 0;
    const trialText =
      days <= 1
        ? 'Votre essai gratuit se termine demain. Après expiration : lecture seule jusqu’à activation commerciale.'
        : `Essai gratuit — ${days} jour${days > 1 ? 's' : ''} restant${days > 1 ? 's' : ''}. Après l’essai : lecture seule jusqu’à facturation.`;
    parts.push(
      `<p style="margin:0 0 12px;padding:10px 12px;border-radius:6px;border:1px solid rgba(0,128,128,0.35);background:#f0fdfa;color:#0a121e;font-size:13px">${trialText}</p>`,
    );
  }

  const intro = opts.cabinetMode
    ? 'Bienvenue sur Nexxus ERP en mode cabinet. Gérez vos entreprises clientes depuis le portefeuille.'
    : 'Bienvenue sur Nexxus ERP. Voici les premières étapes pour démarrer sereinement.';
  parts.push(`<p style="margin:0 0 10px;color:#374151;font-size:14px">${intro}</p>`);

  parts.push(`
    <strong style="color:#008080;display:block;margin-bottom:6px">Premiers pas</strong>
    <ul style="margin:0;padding-left:0;list-style:none;font-size:13px;color:#0a121e">
      ${checklistItem(progress.entreprise, 'Renseigner Mon entreprise')}
      ${checklistItem(progress.salarie, 'Créer le premier salarié')}
      ${checklistItem(progress.bulletin, 'Générer un premier bulletin')}
    </ul>
  `);

  return parts.join('');
}

/** Modale unique au 1er lancement (style Protime) : essai + checklist, sans bandeau permanent. */
export function maybeShowWelcomeModal(host: HTMLElement, opts: WelcomeModalOptions): void {
  const progress = getOnboardingProgress();
  const allDone = progress.entreprise && progress.salarie && progress.bulletin;
  if (allDone && !opts.expired && !opts.trial) {
    dismissOnboarding();
    return;
  }
  if (loadSeen() && !opts.expired) return;

  host.querySelector('#nexxus-welcome-overlay')?.remove();

  const overlay = document.createElement('div');
  overlay.id = 'nexxus-welcome-overlay';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-labelledby', 'nexxus-welcome-title');
  overlay.style.cssText =
    'position:fixed;inset:0;z-index:10000;background:rgba(10,18,30,0.55);display:flex;align-items:center;justify-content:center;padding:16px;';

  const card = document.createElement('div');
  card.style.cssText =
    'max-width:480px;width:100%;background:#fff;border-radius:10px;padding:22px 24px;box-shadow:0 12px 40px rgba(0,0,0,0.25);color:#0a121e;';

  const copy = opts.expired ? expiredLicenceCopy(opts.trial !== false) : null;
  const title = copy?.title ?? 'Bienvenue sur Nexxus ERP';
  const startLabel = opts.expired ? 'Continuer en lecture seule' : 'Commencer';
  card.innerHTML = `
    <h2 id="nexxus-welcome-title" style="margin:0 0 14px;font-size:18px;color:#0a121e">${title}</h2>
    <div id="nexxus-welcome-body"></div>
    <div style="display:flex;justify-content:flex-end;gap:8px;margin-top:18px">
      ${
        opts.expired
          ? `<button type="button" id="nexxus-welcome-subscribe" class="btn btn-primary">${copy?.cta ?? ''}</button>`
          : ''
      }
      <button type="button" id="nexxus-welcome-start" class="btn ${opts.expired ? 'btn-secondary' : 'btn-primary'}">${startLabel}</button>
    </div>
  `;

  const body = card.querySelector('#nexxus-welcome-body');
  if (body) body.innerHTML = buildBody(progress, opts);

  const close = (): void => {
    markSeen();
    if (allDone) dismissOnboarding();
    overlay.remove();
  };

  card.querySelector('#nexxus-welcome-subscribe')?.addEventListener('click', () => {
    void openLicenceSubscribePage();
  });
  card.querySelector('#nexxus-welcome-start')?.addEventListener('click', close);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });

  overlay.appendChild(card);
  host.appendChild(overlay);
}
